import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { applyBrand, buildTheme, DEFAULT_PALETTE, PALETTE_KEYS } from "./theme";

interface Ctx { palette: string; setPalette: (key: string) => void; keys: string[]; }
const ThemeModeContext = createContext<Ctx | undefined>(undefined);

const STORAGE_KEY = "brandPalette";
function readInitial(): string {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && PALETTE_KEYS.includes(v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_PALETTE;
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [palette, setPaletteState] = useState<string>(readInitial);

  // Keep the live BRAND object in sync BEFORE children render.
  applyBrand(palette);

  const setPalette = useCallback((key: string) => {
    if (!PALETTE_KEYS.includes(key)) return;
    applyBrand(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
    setPaletteState(key);
  }, []);

  const theme = useMemo(() => buildTheme(palette), [palette]);
  const value = useMemo<Ctx>(() => ({ palette, setPalette, keys: PALETTE_KEYS }), [palette, setPalette]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export function useThemeMode(): Ctx {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used within <ThemeModeProvider>");
  return ctx;
}
